/**
 * Derived recording key - the per-recording-item key concern.
 *
 * Given the raw `master_key` and a recording id, derives the per-recording key
 * `rec_key = HKDF-SHA256(master_key, salt=deviceId, info=recId)` and slices the
 * AES-128 video key / AES-256 image key from it. The master key itself is owned
 * by `master-key-management.ts`.
 */

import { masterKeyToBytes } from './master-key-management';

/** AES-128 key length in bytes. */
const AES_128_KEY_LENGTH = 16;

/** Byte length of the per-recording key `rec_key` derived via HKDF-SHA256. */
const REC_KEY_LENGTH = 32;

/**
 * Derives the per-recording key `rec_key` via
 * `HKDF-SHA256(ikm = base64decode(master_key), salt = utf8(deviceId), info = utf8(recId))`.
 *
 * @param masterKey - The base64 `master_key` secret (input keying material).
 * @param deviceId - The device id / MAC (`device.id`) used as the HKDF salt.
 * @param recId - The recording id used as the HKDF info.
 * @returns The 32-byte `rec_key`, or `null` when `master_key` is invalid.
 */
export const deriveRecordingKey = async (
	masterKey: string,
	deviceId: string,
	recId: string,
): Promise<Uint8Array | null> => {
	const ikm = masterKeyToBytes(masterKey);
	if (!ikm) return null;

	const encoder = new TextEncoder();
	const baseKey = await crypto.subtle.importKey('raw', ikm as BufferSource, 'HKDF', false, [
		'deriveBits',
	]);
	const bits = await crypto.subtle.deriveBits(
		{
			name: 'HKDF',
			hash: 'SHA-256',
			salt: encoder.encode(deviceId) as BufferSource,
			info: encoder.encode(recId) as BufferSource,
		},
		baseKey,
		REC_KEY_LENGTH * 8,
	);
	return new Uint8Array(bits);
};

/**
 * Derives the AES-128 video key: the first 16 bytes of the per-recording `rec_key`.
 *
 * @param masterKey - The base64 `master_key` secret.
 * @param deviceId - The device id / MAC (`device.id`) used as the HKDF salt.
 * @param recId - The recording id used as the HKDF info.
 * @returns The 16 key bytes, or `null` when `recId`/`master_key` is missing or invalid.
 */
export const deriveVideoKey = async (
	masterKey: string,
	deviceId: string,
	recId?: string,
): Promise<Uint8Array | null> => {
	if (!recId) return null;
	const recKey = await deriveRecordingKey(masterKey, deviceId, recId);
	return recKey ? recKey.slice(0, AES_128_KEY_LENGTH) : null;
};

/**
 * Derives the AES-256 image key: the full 32 bytes of the per-recording `rec_key`.
 *
 * @param masterKey - The base64 `master_key` secret.
 * @param deviceId - The device id / MAC (`device.id`) used as the HKDF salt.
 * @param recId - The recording id used as the HKDF info.
 * @returns The 32 key bytes, or `null` when `recId`/`master_key` is missing or invalid.
 */
export const deriveImageKey = async (
	masterKey: string,
	deviceId: string,
	recId?: string,
): Promise<Uint8Array | null> => {
	if (!recId) return null;
	return deriveRecordingKey(masterKey, deviceId, recId);
};
